import { ContainerAppsAPIClient } from "@azure/arm-appcontainers";
import { DefaultAzureCredential } from "@azure/identity";
import { select } from "@inquirer/prompts";
import { LocalCliError } from "./errors.js";
import { getAzAccount } from "./auth.js";

export interface DiscoveryResult {
  gatewayUrl: string;
  gatewayClientId: string;
  gatewayTenantId: string;
  subscriptionId: string;
  containerAppResourceId: string;
  /** From the gateway's FOUNDRY_PROJECT_ENDPOINT env. Absent on older deploys. */
  projectEndpoint?: string;
}

interface CandidateContainerApp {
  resourceId: string;
  name: string;
  resourceGroup: string;
  location: string;
  fqdn?: string;
  envVars: Map<string, string>;
}

/**
 * Find the m8t gateway in Azure and return everything we need to
 * authenticate against it.
 */
export async function discoverGateway(opts: {
  subscriptionId?: string;
  interactive: boolean;
  resourceGroup?: string;
}): Promise<DiscoveryResult> {
  const subscriptionId = opts.subscriptionId ?? (await getAzAccount()).subscriptionId;
  if (!subscriptionId) {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_NO_SUBSCRIPTION",
      message: "Could not determine an active Azure subscription.",
      hint: "Run 'az account set --subscription <name-or-id>' or pass --subscription.",
    });
  }

  const client = new ContainerAppsAPIClient(new DefaultAzureCredential(), subscriptionId);

  const candidates: CandidateContainerApp[] = [];
  try {
    for await (const app of client.containerApps.listBySubscription()) {
      if (app.tags?.m8t !== "gateway") continue;
      const fqdn = app.configuration?.ingress?.fqdn;
      const envs = app.template?.containers?.[0]?.env ?? [];
      const envMap = new Map<string, string>();
      for (const e of envs) {
        if (e.name && typeof e.value === "string") {
          envMap.set(e.name, e.value);
        }
      }
      candidates.push({
        resourceId: app.id ?? "",
        name: app.name ?? "",
        resourceGroup: (app.id ?? "").split("/resourceGroups/")[1]?.split("/")[0] ?? "",
        location: app.location,
        fqdn,
        envVars: envMap,
      });
    }
  } catch (e) {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_ARM_FAILED",
      message: `Failed to query Container Apps in subscription ${subscriptionId}: ${(e as Error).message}`,
      hint: "Verify 'az account show' shows the right subscription and you have Reader access to it.",
      cause: e,
    });
  }

  if (candidates.length === 0) {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_ZERO",
      message: `No m8t deployment found in subscription ${subscriptionId}.`,
      hint: "If the deployment is in another subscription, run 'az account set --subscription <name>'.",
    });
  }

  let pool = candidates;
  if (opts.resourceGroup) {
    const want = opts.resourceGroup.toLowerCase();
    pool = candidates.filter((c) => c.resourceGroup.toLowerCase() === want);
    if (pool.length === 0) {
      throw new LocalCliError({
        code: "GATEWAY_DISCOVERY_RG_NOT_FOUND",
        message: `No m8t gateway found in resource group '${opts.resourceGroup}' (subscription ${subscriptionId}).`,
        hint: `m8t gateways in this subscription: ${candidates.map((c) => c.resourceGroup).join(", ")}.`,
      });
    }
  }

  let chosen: CandidateContainerApp;
  if (pool.length === 1) {
    chosen = pool[0];
  } else if (opts.interactive) {
    const resourceId = await select({
      message: `Multiple m8t deployments found in subscription ${subscriptionId}. Choose one:`,
      choices: pool.map((c) => ({
        name: `${c.name}  (rg=${c.resourceGroup}, ${c.location})`,
        value: c.resourceId,
      })),
    });
    const found = pool.find((c) => c.resourceId === resourceId);
    if (!found) {
      throw new LocalCliError({
        code: "GATEWAY_DISCOVERY_SELECTION_FAILED",
        message: "No deployment selected.",
      });
    }
    chosen = found;
  } else {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_MULTIPLE",
      message: `Multiple m8t gateways found in subscription ${subscriptionId}: ${pool
        .map((c) => `${c.name} (rg=${c.resourceGroup}, ${c.location})`)
        .join("; ")}.`,
      hint: `Re-run with --resource-group <rg> to pick one (e.g. --resource-group ${pool[0].resourceGroup}).`,
    });
  }

  const fqdn = chosen.fqdn;
  if (!fqdn) {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_NO_FQDN",
      message: `Container App '${chosen.name}' has no ingress FQDN.`,
      hint: "Verify the deployment's ingress is configured (external=true).",
    });
  }

  const gatewayClientId = chosen.envVars.get("AZURE_CLIENT_ID");
  const gatewayTenantId = chosen.envVars.get("AZURE_TENANT_ID");
  if (!gatewayClientId || !gatewayTenantId) {
    throw new LocalCliError({
      code: "GATEWAY_DISCOVERY_MISSING_ENV",
      message: `Container App '${chosen.name}' is missing AZURE_CLIENT_ID or AZURE_TENANT_ID env vars.`,
      hint: "Verify the deployment via 'az containerapp show -n <name> -g <rg> --query properties.template.containers[0].env'. Note: env vars set via secretRef are not supported by auto-discovery — set them as direct values.",
    });
  }

  const projectEndpoint = chosen.envVars.get("FOUNDRY_PROJECT_ENDPOINT");

  return {
    gatewayUrl: `https://${fqdn}`,
    gatewayClientId,
    gatewayTenantId,
    subscriptionId,
    containerAppResourceId: chosen.resourceId,
    projectEndpoint,
  };
}
